import { AcquisitionYesNo } from '../../../../services/api/acquisition/acquisitionOperation';
import {
  CdqEmployeesNumber,
  CdqOccupation,
  CdqTenureBand,
} from '../../../../services/api/acquisition/cdqAcquisition';

interface CdqOption<TValue extends string> {
  value: TValue;
  label: string;
}

/**
 * The canonical occupations are identifiers rather than prose, so the operator
 * reads the public product's own labels instead.
 */
export const CDQ_OCCUPATION_LABEL: Record<CdqOccupation, string> = {
  dipendente_pubblico: 'Dipendente Statale',
  dipendente_privato: 'Dipendente Privato',
  dipendente_parapubblico: 'Dipendente Parastatale',
  pensionato: 'Pensionato',
};

/**
 * Whom a Cessione del quinto is acquired for, in the order the public funnel
 * lists them. `autonomo` is not among them, because the backend refuses it and
 * offering a choice only to reject it wastes an assisted call.
 */
export const CDQ_OCCUPATION_OPTIONS: CdqOption<CdqOccupation>[] = (
  Object.keys(CDQ_OCCUPATION_LABEL) as CdqOccupation[]
).map((value) => ({ value, label: CDQ_OCCUPATION_LABEL[value] }));

/**
 * The backend's own employer-size labels. The public UI shows aliases that do
 * not match the contract; reproducing them here would put a second vocabulary
 * between the operator's answer and the value that is judged.
 */
export const CDQ_EMPLOYEES_NUMBER_OPTIONS: CdqOption<CdqEmployeesNumber>[] = (
  [
    'Meno di 15 dip',
    'Più di 15 dip',
  ] as const satisfies readonly CdqEmployeesNumber[]
).map((value) => ({ value, label: value }));

/** The canonical tenure bands; the identifier is already what one reads. */
export const CDQ_TENURE_OPTIONS: CdqOption<CdqTenureBand>[] = [
  '1 anno o meno',
  '2 anni',
  '3 anni o più',
].map((value) => ({ value: value as CdqTenureBand, label: value }));

/**
 * How many times a year the salary is paid. The draft holds the choice as text
 * because that is what a select exchanges; the command sends the number.
 */
export const CDQ_SALARY_INSTALMENTS_OPTIONS: CdqOption<string>[] = [
  '12',
  '13',
  '14',
].map((value) => ({ value, label: value }));

/** `si`/`no` is the wire vocabulary; Sì/No is what the operator picks. */
export const CDQ_YES_NO_OPTIONS: CdqOption<AcquisitionYesNo>[] = [
  { value: 'si', label: 'Sì' },
  { value: 'no', label: 'No' },
];

export const cdqYesNoLabel = (value: AcquisitionYesNo) =>
  value === 'si' ? 'Sì' : 'No';
